import type { CellContext } from '@tanstack/react-table';
import { useUpdateFeatureFlagsMutation } from 'gcs-common/clients/api/entities/feature-flags/feature-flags-api';
import type { FetchFeatureFlagsResponseData } from 'gcs-common/clients/api/entities/feature-flags/feature-flags-api-helpers';
import { getTenantSelectOptions } from 'gcs-common/slices/administration/tenants/tenantsSelectors';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import EditingCell from '../EditingCell/EditingCell';

interface OptionType {
  value: string;
  label: string;
}

interface FeatureFlagTenantsCellProps {
  cell: CellContext<FetchFeatureFlagsResponseData['data'][number], unknown>['cell'];
  value: FetchFeatureFlagsResponseData['data'][number]['tenants'];
}

const FeatureFlagTenantsCell = (props: FeatureFlagTenantsCellProps) => {
  const { cell, value: tenants } = props;
  const [allTenantOptions, setAllTenantOptions] = useState<OptionType[]>([]);
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [options, setOptions] = useState<OptionType[]>([]);

  const { mutateAsync, isPending } = useUpdateFeatureFlagsMutation();

  // TODO: migrate soon
  const tenantSelectOptions: { id: string; friendlyName: string }[] = useSelector(getTenantSelectOptions);

  const featureFlagId = cell.row.original.id;

  useEffect(() => {
    const fetchOptions = async () => {
      const formattedOptions = tenantSelectOptions?.map((tenant) => ({
        label: tenant.friendlyName,
        value: tenant.id,
      }));
      setAllTenantOptions(formattedOptions);
    };

    fetchOptions();
  }, [dispatch, tenantSelectOptions]);

  const setInitialOptions = () => {
    const initialOptions = tenants.map((tenant) => ({ label: tenant.friendlyName, value: tenant.id }));
    setOptions(initialOptions);
  };

  useEffect(() => {
    setInitialOptions();
  }, []);

  const save = async () => {
    const tenantIds = options?.map((option) => option.value) || [];
    await mutateAsync({
      path: {
        feature_flag_id: featureFlagId,
      },
      body: {
        tenantIds,
      },
    });
  };

  const cancel = () => {
    setInitialOptions();
  };

  return (
    <EditingCell onSave={save} isEditing={isEditing} setIsEditing={setIsEditing} onCancel={cancel} disabled={isPending}>
      <Select
        isMulti
        options={allTenantOptions}
        isLoading={!allTenantOptions.length || isPending}
        isDisabled={!isEditing || isPending}
        value={options}
        onChange={(selected) => {
          setOptions([...selected]);
        }}
        styles={{ container: (base) => ({ ...base, minWidth: '300px' }) }}
      />
    </EditingCell>
  );
};

export default FeatureFlagTenantsCell;
